<script setup lang="ts">
const features = {
  pos: {
    title: 'Point of Sale Features',
    icon: '🏪',
    description: 'A modern, intuitive POS system that streamlines your sales operations and enhances customer experience.',
    items: [
      {
        icon: '🖥️',
        title: 'Smart Interface',
        description: 'User-friendly touchscreen interface designed for speed and efficiency',
      },
      {
        icon: '🔍',
        title: 'Quick Search',
        description: 'Advanced product search with barcode scanning capabilities',
      },
      {
        icon: '👥',
        title: 'Customer Management',
        description: 'Comprehensive customer profiles and loyalty program integration',
      },
      {
        icon: '💳',
        title: 'Payment Flexibility',
        description: 'Support for multiple payment methods including cards, digital wallets, and split payments',
      },
      {
        icon: '🎯',
        title: 'Promotions',
        description: 'Flexible discount system and promotional campaign management',
      }
    ]
  },
  inventory: {
    title: 'Inventory Features',
    icon: '📦',
    description: 'Powerful inventory management tools that give you complete control over your stock.',
    items: [
      {
        icon: '📊',
        title: 'Real-time Tracking',
        description: 'Live inventory monitoring across all your locations',
      },
      {
        icon: '🔄',
        title: 'Auto Reordering',
        description: 'Smart reorder points and automated purchase order generation',
      },
      {
        icon: '🏢',
        title: 'Multi-location',
        description: 'Seamless management of inventory across multiple warehouses',
      },
      {
        icon: '📝',
        title: 'Batch Tracking',
        description: 'Detailed batch and serial number tracking system',
      },
      {
        icon: '📈',
        title: 'Analytics',
        description: 'Advanced inventory forecasting and analysis tools',
      }
    ]
  },
  accounting: {
    title: 'Accounting Features',
    icon: '📊',
    description: 'Comprehensive financial management tools that keep your books accurate and up-to-date.',
    items: [
      {
        icon: '📚',
        title: 'Auto Bookkeeping',
        description: 'Automated transaction recording and reconciliation',
      },
      {
        icon: '📱',
        title: 'Mobile Access',
        description: 'Secure access to financial data from any device',
      },
      {
        icon: '🌐',
        title: 'Multi-currency',
        description: 'Support for multiple currencies with real-time exchange rates',
      },
      {
        icon: '📋',
        title: 'Tax Management',
        description: 'Automated tax calculations and reporting system',
      },
      {
        icon: '📊',
        title: 'Financial Reports',
        description: 'Comprehensive reporting suite with customizable dashboards',
      }
    ]
  }
}
</script>

<template>
  <section class="py-16 bg-white dark:bg-gray-900">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-12">
        <h2 class="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Comprehensive Feature Set
        </h2>
        <p class="text-lg text-gray-600 dark:text-gray-400">
          Everything you need to run your business efficiently
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div
          v-for="(category, key) in features"
          :key="key"
          class="bg-gray-50 dark:bg-gray-800 rounded-xl p-6"
        >
          <!-- Category Header -->
          <div class="flex items-center gap-3 mb-4">
            <span class="text-3xl">{{ category.icon }}</span>
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                {{ category.title }}
              </h3>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {{ category.description }}
              </p>
            </div>
          </div>

          <!-- Feature List -->
          <div class="space-y-6 mt-8">
            <div
              v-for="(feature, index) in category.items"
              :key="index"
              class="flex gap-4"
            >
              <div class="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                <span class="text-xl">{{ feature.icon }}</span>
              </div>
              <div>
                <h4 class="font-medium text-gray-900 dark:text-white">
                  {{ feature.title }}
                </h4>
                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {{ feature.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>